/**
 * Turns a mirrored Skool teaser into something a model will actually quote.
 *
 * The importer writes `summary` by taking the opening of the post body and cutting it at
 * a character budget, which leaves strings like:
 *
 *   "…a secure server-side bridge between a user-friendly application and the…"
 *
 * The 2026-09-04 GEO audit called this out on 171 pages: a language model will not cite
 * a fragment, and a truncated sentence reads to a quality classifier as a broken page.
 * The fix that matters is upstream — the importer should write a real 150-to-200 word
 * public summary — but this at least guarantees that whatever lands here ends on a full
 * stop and never mid-clause.
 *
 * Deliberately conservative: it only ever removes text. It will not invent a sentence,
 * and if the input has no sentence boundary at all it falls back to a whole-word cut
 * plus a period rather than guessing where a thought ended.
 */

const ELLIPSES = /[\s]*(?:…|\.\.\.)+[\s]*$/;
/** Trailing connectives that make a "complete" sentence still read as cut off. */
const DANGLING =
  /\s+(?:and|or|but|with|the|a|an|of|to|for|in|on|at|by|from|that|which|as|is|are|these|those|this|such|its|their|our|your|his|her|it)$/i;

export const cleanSummary = (raw: string | null | undefined, maxChars = 320): string => {
  if (!raw) return "";

  const text = String(raw).replace(/\s+/g, " ").replace(ELLIPSES, "").trim();
  if (!text) return "";

  // Short enough to keep whole: just make sure it terminates.
  if (text.length <= maxChars) return terminate(text);

  const window_ = text.slice(0, maxChars);
  const lastStop = Math.max(window_.lastIndexOf(". "), window_.lastIndexOf("! "), window_.lastIndexOf("? "));

  // Only trust a sentence boundary if it leaves a summary worth reading. Cutting a
  // 320-character teaser down to 40 because the first sentence was "Step 1." is worse
  // than the fragment it replaced.
  if (lastStop >= Math.min(120, maxChars / 2)) return window_.slice(0, lastStop + 1).trim();

  const lastSpace = window_.lastIndexOf(" ");
  return terminate(window_.slice(0, lastSpace > 0 ? lastSpace : window_.length).trim());
};

const terminate = (s: string): string => {
  // Run to a fixed point: teasers routinely end on a run of connectives ("…and the",
  // "…of a"), and stripping one still leaves "…application and." on the page.
  let trimmed = s.trim();
  for (let i = 0; i < 6; i++) {
    const next = trimmed.replace(DANGLING, "").replace(/[,;:]$/, "").trim();
    if (next === trimmed) break;
    trimmed = next;
  }
  if (!trimmed) return "";
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

/** A one-line version for cards and meta descriptions, where 320 chars is too long. */
export const cleanSummaryShort = (raw: string | null | undefined, maxChars = 165): string =>
  cleanSummary(raw, maxChars);

/**
 * A description for a page whose real body is gated. Names the author and the date so
 * the snippet carries provenance even when the write-up itself does not.
 */
export const gatedDescription = (
  summary: string | null | undefined,
  opts: { author?: string | null; date?: string | null; fallback: string },
): string => {
  const body = cleanSummaryShort(summary);
  if (!body) return opts.fallback;
  const by = opts.author ? ` By ${opts.author}.` : "";
  return `${body}${by}`.slice(0, 300);
};
